/**
 * Icons by Softmax semantic name. Code never names a Lucide icon directly; swapping the set means editing this table.
 * A new name goes in `spec/icons.ts` too. See docs/design/ui-principles.md, Iconography.
 */
import {
  ArrowDown,
  GripHorizontal,
  MousePointer2,
  PanelLeftClose,
  PanelLeftOpen,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  Bookmark,
  Bot,
  Brain,
  BrainCircuit,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  CircleUser,
  Clock,
  CodeXml,
  Copy,
  ExternalLink,
  Globe,
  LayoutDashboard,
  LoaderCircle,
  LogIn,
  LogOut,
  Maximize2,
  Menu,
  MessageSquare,
  MessageSquareText,
  Mic,
  MicOff,
  Minimize2,
  PanelLeft,
  Pause,
  PenLine,
  Play,
  Plus,
  Radio,
  Repeat,
  RotateCw,
  Swords,
  Trash2,
  Trophy,
  User,
  UserRound,
  Volume2,
  VolumeX,
  X,
  // The agent customizer.
  AudioLines,
  Ban,
  Dices,
  Footprints,
  Hand,
  Lock,
  Palette,
  RotateCcw,
  Shirt,
  Smile,
  Sparkles,
  Undo2,
  // Participate: creating a policy.
  CircleCheck,
  Container,
  FileCode,
  FolderOpen,
  Save,
  SquareTerminal,
  Star,
  TriangleAlert,
  Upload,
  // Coworlds: browsing.
  Search,
  TrendingDown,
  TrendingUp,
  Minus,
  SlidersHorizontal,
  // The training ground: what a run spends.
  Coins,
  // The policy workspace's Code tab: its file list.
  Folder,
  // The Discovery tab: research, its neurons, and the edges it finds.
  Telescope,
  Atom,
  Zap,
  Target,
  ChartLine,
  Pin,
  Shapes,
  // The Blueprints tab: a policy's plan, drawn out.
  DraftingCompass,
  // Page views: the page itself (UI), arranging it (Edit), and a part's handle.
  AppWindow,
  LayoutTemplate,
  GripVertical,
  type LucideIcon,
} from 'lucide-react'
import type { IconName } from './spec/icons.ts'

export type { IconName }

const ICONS = {
  // From the internal icon catalog.
  sort: ArrowUpDown,
  'sort-ascending': ArrowUp,
  'sort-descending': ArrowDown,
  check: Check,
  'panel-left': PanelLeft,
  menu: Menu,
  expand: Maximize2,
  collapse: Minimize2,
  pause: Pause,
  play: Play,
  microphone: Mic,
  'microphone-off': MicOff,
  pen: PenLine,
  bookmark: Bookmark,
  note: MessageSquareText,
  user: User,
  help: CircleHelp,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  close: X,
  copy: Copy,
  delete: Trash2,
  // Softmax primitives [proposal].
  dashboard: LayoutDashboard,
  policy: Brain,
  coworld: Globe,
  leaderboard: Trophy,
  cogent: Bot,
  opponent: Swords,
  player: UserRound,
  // Client actions and states [proposal].
  back: ArrowLeft,
  send: ArrowUp,
  add: Plus,
  refresh: RotateCw,
  external: ExternalLink,
  'sign-in': LogIn,
  'sign-out': LogOut,
  // The navigation's way to your profile, apart from Players' person.
  account: CircleUser,
  // The navigation stack's label modes (labels on hover, always, never) and its drag handle.
  'nav-auto': MousePointer2,
  'nav-labels': PanelLeftOpen,
  'nav-icons': PanelLeftClose,
  grip: GripHorizontal,
  live: Radio,
  time: Clock,
  thread: MessageSquare,
  loading: LoaderCircle,
  'sound-on': Volume2,
  'sound-off': VolumeX,
  // The agent dock: its mind, look, and personality [proposal].
  mind: BrainCircuit,
  personality: Smile,
  // The agent customizer [proposal].
  customize: Shirt,
  presets: Sparkles,
  head: Smile,
  body: Shirt,
  hands: Hand,
  legs: Footprints,
  colors: Palette,
  voice: AudioLines,
  lock: Lock,
  undo: Undo2,
  reset: RotateCcw,
  randomize: Dices,
  none: Ban,
  // Participate: creating a policy [proposal].
  done: CircleCheck,
  docker: Container,
  file: FileCode,
  folder: FolderOpen,
  save: Save,
  terminal: SquareTerminal,
  recommended: Star,
  warning: TriangleAlert,
  upload: Upload,
  search: Search,
  'trend-up': TrendingUp,
  'trend-down': TrendingDown,
  steady: Minus,
  filter: SlidersHorizontal,
  // The training ground: XP credits a training run spends [proposal].
  credits: Coins,
  // Workers: the agent's brain now → bigger [proposal].
  forward: ArrowRight,
  // Policy workspace tabs: a custom tab (built with code) and the R/L loop tab [proposal].
  code: CodeXml,
  loop: Repeat,
  // The Code tab's file list: a folder that isn't open [proposal].
  'folder-closed': Folder,
  // The Discovery tab: research, a policy's neurons (its building blocks), and an edge it found [proposal].
  discovery: Telescope,
  neuron: Atom,
  edge: Zap,
  // The Goal loop tab: a target for a neuron, worked on until it's met [proposal].
  goal: Target,
  // Intermediate representations: a neuron's charts [proposal].
  chart: ChartLine,
  // Tabs: pinning one, and choosing a custom tab's icon [proposal].
  pin: Pin,
  shapes: Shapes,
  // The Blueprints tab: a policy's neurons, findings, and goals on one canvas [proposal].
  blueprint: DraftingCompass,
  // The page as you see it, beside its blocks and code [proposal].
  page: AppWindow,
  layout: LayoutTemplate,
  drag: GripVertical,
} satisfies Record<IconName, LucideIcon>

export interface IconProps {
  name: IconName
  /** px. Default 16. */
  size?: number
  className?: string
  /** Accessible name. Omit for decorative icons next to a text label. */
  label?: string
}

export function Icon({ name, size = 16, className, label }: IconProps) {
  const Glyph = ICONS[name]
  return (
    <Glyph
      size={size}
      className={className}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
    />
  )
}
